import React from 'react';
const { useState, useEffect } = React;
import { coachCall } from './shared.jsx';
import { Ring, Bar, MainMetric, Milestones, ScheduledButton, muted, fmtWhen, fmtDay, openSession, openActivities, openReview, openThread } from './ProgressParts.jsx';
import { METRIC_COUNTS } from '../../journey.js';

// «Progreso» of a team process (the coach only; coaching-admin › teamProgress decides what comes): the group's
// progress and one row per participant, in alphabetical order, with the indicators that participant sees in Mi
// recorrido and what is pending for the coach. Every action opens exactly its session, submission or conversation,
// with the same notices as «Por atender» (no new state). No global percentage, ranking or score. This is the coach's
// panel: whoever hires or leads never sees it.

const cardSt = { background: 'var(--globant-card)', border: '1px solid var(--globant-border)', borderRadius: 14, padding: 16 };
const small = { fontSize: 11, padding: '3px 9px' };
const pill = (tone) => ({ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap', background: tone === 'warn' ? '#FBF0DC' : '#EEF1F0', color: tone === 'warn' ? '#6B4510' : '#33413E' });
const th = { textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--globant-muted)', padding: '6px 8px', borderBottom: '1px solid var(--globant-border)', verticalAlign: 'bottom' };
const td = { padding: '8px', borderBottom: '1px solid var(--globant-border)', verticalAlign: 'top', fontSize: 13 };

function Mini({ m }) {
  if (!m) return <span style={muted}>Sin total definido</span>;
  return <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}><Ring metric={m} size={38} /><span style={{ fontSize: 12 }}>{m.label}</span></div>;
}

function Pending({ r, processId, onChanged, compact = false }) {
  const toSchedule = new Set(r.toSchedule || []);
  const items = [
    ...r.upcoming.filter(s => toSchedule.has(s.id)).map(s => (
      <div key={`b-${s.id}`} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={pill('warn')}>Reserva por agendar</span><span style={muted}>{fmtWhen(s.startsAt)}</span>
        <ScheduledButton sessionId={s.id} onDone={onChanged} />
        <button type="button" className="action-btn" style={small} onClick={() => openSession(processId, s.id)}>Abrir sesión</button>
      </div>
    )),
    ...r.reviews.map(x => (
      <div key={`r-${x.submissionId}`} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={pill()}>Entrega</span><span style={{ fontSize: 12 }}>{x.lessonTitle}{x.attempt > 1 ? ` · v${x.attempt}` : ''}</span>
        <button type="button" className="action-btn btn-primary" style={small} onClick={() => openReview(x)}>Revisar</button>
      </div>
    )),
    ...r.messages.map(x => (
      <div key={`m-${x.id}`} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={pill()}>{x.count} sin leer</span>
        <button type="button" className="action-btn" style={small} onClick={() => openThread(x)}>Abrir conversación</button>
      </div>
    )),
  ];
  if (!items.length) return <span style={muted}>{compact ? 'Nada por atender' : '—'}</span>;
  return <div style={{ display: 'grid', gap: 6 }}>{items}</div>;
}

function NextCell({ r, processId }) {
  const s = r.upcoming[0];
  return (
    <div style={{ display: 'grid', gap: 4, fontSize: 12 }}>
      {r.next ? <span>{r.next.title}{r.next.when ? ` · ${fmtWhen(r.next.when)}` : ''}</span> : <span style={muted}>Sin próximo paso</span>}
      {s && <button type="button" className="action-btn" style={{ ...small, justifySelf: 'start' }} onClick={() => openSession(processId, s.id)}>Abrir sesión {s.number || ''}</button>}
      {r.activities.length > 0 && <button type="button" className="action-btn" style={{ ...small, justifySelf: 'start' }} onClick={() => openActivities(processId)}>{r.activities.length} actividad{r.activities.length === 1 ? '' : 'es'} pendiente{r.activities.length === 1 ? '' : 's'}{r.activities[0].dueOn ? ` · vence ${fmtDay(r.activities[0].dueOn)}` : ''}</button>}
    </div>
  );
}

const openPerson = (personId) => window.dispatchEvent(new CustomEvent('oike:navigate', { detail: { page: 'coaching', personId } }));
const coursesMetric = (r) => r.courses.length === 1 ? r.courses[0].metric : null;

export default function TeamProgress({ processId }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [narrow, setNarrow] = useState(() => (globalThis.window ? window.innerWidth < 760 : false));
  useEffect(() => { const h = () => setNarrow(window.innerWidth < 760); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  const load = () => coachCall({ action: 'teamProgress', processId }).then(x => { setD(x); setError(''); }).catch(e => setError(e.message));
  useEffect(() => { load(); }, [processId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (error) return <div style={cardSt} role="alert">{error} <button className="action-btn" onClick={load}>Reintentar</button></div>;
  if (!d) return <div style={cardSt}>Cargando…</div>;
  const g = d.group;
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <section style={cardSt} aria-label="Progreso del equipo">
        <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {g.sessions ? <MainMetric metric={g.sessions} title="Sesiones grupales" /> : <span style={muted}>Sin sesiones grupales en el programa.</span>}
          {g.goals ? <MainMetric metric={g.goals} title="Objetivos compartidos" /> : <span style={muted}>Sin objetivos compartidos acordados.</span>}
          <div style={{ fontSize: 13, minWidth: 0 }}>
            <b style={{ display: 'block' }}>Próxima sesión grupal</b>
            {g.next ? <><span>{fmtWhen(g.next.startsAt)} · {g.next.title || `Sesión ${g.next.number || ''}`}</span> <button type="button" className="action-btn" style={small} onClick={() => openSession(processId, g.next.id)}>Abrir</button></> : <span style={muted}>Ninguna agendada.</span>}
            {g.messages.map(x => <div key={x.id} style={{ marginTop: 6 }}><span style={pill()}>Canal del equipo · {x.count} sin leer</span> <button type="button" className="action-btn" style={small} onClick={() => openThread(x)}>Abrir conversación</button></div>)}
          </div>
        </div>
        {g.milestones.length > 0 && (
          <details style={{ marginTop: 10 }}>
            <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--globant-green)' }}>Hitos del equipo ({g.milestones.filter(s => s.state === 'done').length} de {g.milestones.length})</summary>
            <div style={{ marginTop: 8 }}><Milestones steps={g.milestones} title="Hitos del equipo" /></div>
          </details>
        )}
      </section>

      <section style={cardSt} aria-label="Participantes">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'baseline', marginBottom: 8 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>Participantes ({d.rows.length})</h3>
          <span style={muted}>En orden alfabético. Lo mismo que cada persona ve en Mi recorrido.</span>
        </div>
        {!d.rows.length && <span style={muted}>Todavía no hay participantes.</span>}
        {narrow ? (
          <div style={{ display: 'grid', gap: 10 }}>
            {d.rows.map(r => (
              <div key={r.memberId} style={{ border: '1px solid var(--globant-border)', borderRadius: 12, padding: 12, display: 'grid', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'center' }}>
                  <button type="button" className="action-btn" style={{ ...small, fontWeight: 700 }} onClick={() => r.personId && openPerson(r.personId)}>{r.name}</button>
                </div>
                <Bar metric={r.progress?.individual} title="Sesiones individuales" />
                <Bar metric={r.progress?.activities} title="Actividades propias" />
                <Bar metric={r.progress?.goals} title="Objetivos individuales" />
                {r.courses.map(c => <Bar key={c.id} metric={c.metric} title={c.courseName} />)}
                <NextCell r={r} processId={processId} />
                <Pending r={r} processId={processId} onChanged={load} compact />
              </div>
            ))}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 980 }}>
              <thead><tr>
                <th style={th}>Persona</th>
                <th style={th} title={METRIC_COUNTS.individualSessions}>Sesiones individuales</th>
                <th style={th} title={METRIC_COUNTS.activities}>Actividades propias</th>
                <th style={th} title={METRIC_COUNTS.goals}>Objetivos individuales</th>
                <th style={th} title={METRIC_COUNTS.lessons}>Cursos del proceso</th>
                <th style={th}>Próximo paso</th>
                <th style={th}>Por atender</th>
              </tr></thead>
              <tbody>
                {d.rows.map(r => (
                  <tr key={r.memberId}>
                    <td style={td}><button type="button" className="action-btn" style={{ ...small, fontWeight: 700 }} onClick={() => r.personId && openPerson(r.personId)} title="Abrir su ficha en Personas">{r.name}</button></td>
                    <td style={td}><Mini m={r.progress?.individual} /></td>
                    <td style={td}><Mini m={r.progress?.activities} /></td>
                    <td style={td}><Mini m={r.progress?.goals} /></td>
                    <td style={td}>{!r.courses.length ? <span style={muted}>Ninguno</span> : coursesMetric(r) ? <Mini m={coursesMetric(r)} /> : <div style={{ display: 'grid', gap: 4 }}>{r.courses.map(c => <span key={c.id} style={{ fontSize: 12 }}>{c.courseName}: {c.metric?.label || 'sin lecciones'}</span>)}</div>}</td>
                    <td style={td}><NextCell r={r} processId={processId} /></td>
                    <td style={td}><Pending r={r} processId={processId} onChanged={load} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <details style={{ marginTop: 10 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13 }}>Qué cuenta cada número</summary>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18, display: 'grid', gap: 3, fontSize: 12 }}>
            <li><b>Sesiones individuales:</b> {METRIC_COUNTS.individualSessions}</li>
            <li><b>Actividades propias:</b> {METRIC_COUNTS.activities}</li>
            <li><b>Objetivos individuales:</b> {METRIC_COUNTS.goals}</li>
            <li><b>Cursos:</b> {METRIC_COUNTS.lessons}</li>
          </ul>
        </details>
        <p style={{ ...muted, margin: '8px 0 0' }}>Cada número es «hecho de total». Sin total definido, por ejemplo sin objetivos acordados, no hay indicador. Sin porcentaje global, ranking ni puntaje. Los pendientes son los mismos de «Por atender». Las reflexiones privadas no aparecen.</p>
        {!d.attentionReady && <p role="alert" style={{ ...muted, color: '#9A6212' }}>No se pudieron leer los pendientes ahora: los indicadores sí están al día.</p>}
      </section>
    </div>
  );
}
